import { useDisclosure } from '@chakra-ui/react';
import { IContactRespSchema, MCustomSelectProps } from '@monetize/types/app';
import { forwardRef, useEffect, useMemo, useState } from 'react';
import { CONTACTS } from '../../constants';
import { useACL } from '../../services/acl/acl';
import { ContactFormModal } from '../Contacts/ContactFormModal';
import { contactSelectRenderItem } from '../Contacts/ContactSelect';
import { QuoteSelectInput } from '../Monetize';
import { MCustomSelect } from '../Monetize/MCustomSelect/MCustomSelect';

interface QuoteBasicContactSelectProps extends Omit<
  MCustomSelectProps,
  'value' | 'onChange'
> {
  /**
   * When provided, the select operates in client-side mode and filters this list locally.
   * When omitted (and `accountId` is provided), the select fetches paginated contacts
   * from `/api/accounts/{accountId}/contacts` with debounced server-side search and
   * hydrates the current value via `/api/contacts/{id}`.
   */
  contacts?: IContactRespSchema[];
  value?: IContactRespSchema | null;
  accountId?: string;
  onChange: (contact: IContactRespSchema) => void;
  defaultValues?: Partial<IContactRespSchema>;
  handleOnNewContact?: () => void;
  allowContactCreation?: boolean;
}

const SERVER_SIDE_ADDITIONAL_SEARCH_PARAMS = {
  showInternal: 'false',
  status: 'ACTIVE',
  sort: 'fullName:asc',
  pageSize: '50',
} as const;

export const QuoteBasicContactSelect = forwardRef(
  (
    {
      contacts,
      value,
      onChange,
      accountId,
      defaultValues,
      handleOnNewContact,
      allowContactCreation = true,
      id,
      ...rest
    }: QuoteBasicContactSelectProps,
    ref,
  ) => {
    const isServerSide = !contacts;

    const [query, setQuery] = useState('');
    const [iContacts, setIContacts] = useState(contacts || []);
    const onChangeQuery = (q: string): void => {
      if (q !== query) {
        setQuery(q);
      }
    };
    const { canDo } = useACL();
    const contactModalState = useDisclosure();

    useEffect(() => {
      if (isServerSide) {
        return;
      }
      const list = contacts || [];
      if (query) {
        setIContacts(
          list.filter(
            (c) =>
              (c.fullName &&
                c.fullName.toLowerCase().includes(query.toLowerCase())) ||
              (c.email && c.email.toLowerCase().includes(query.toLowerCase())),
          ),
        );
      } else {
        setIContacts(list);
      }
    }, [contacts, query, isServerSide]);

    const canCreateContact =
      canDo([['account_contacts', 'create']]) &&
      !!accountId &&
      allowContactCreation;

    const handleContactModalClose = async (contact: IContactRespSchema) => {
      contactModalState.onClose();
      onChange(contact);
    };

    const serverSideProps = useMemo(() => {
      if (!isServerSide || !accountId) {
        return null;
      }
      return {
        endpoint: `/api/accounts/${accountId}/contacts`,
        additionalSearchParams: SERVER_SIDE_ADDITIONAL_SEARCH_PARAMS,
        itemSearch: 'fullName',
        getByIdEndpointFn: (id: string) => `/api/contacts/${id}`,
        getByIdRecordId: value?.id,
        infiniteScrolling: true,
      };
    }, [isServerSide, accountId, value?.id]);

    const clientSideProps = isServerSide
      ? null
      : {
          items: iContacts,
          useExternalQuery: true,
          externalQuery: query,
          onChangeQuery,
          skipFilter: true,
        };

    return (
      <>
        <MCustomSelect
          value={value}
          name="contacts"
          id={id}
          inputTestId={id ? `customSelect-${id}` : undefined}
          popOverProps={{
            positioning: { placement: 'bottom-end', sameWidth: false },
          }}
          itemTitle="fullName"
          itemValue="id"
          onChange={(ev: any) => onChange(ev)}
          MainInputComponent={QuoteSelectInput}
          showContentInInput
          useHighlightedItemList
          showItemDivider
          inputProps={{
            autoComplete: 'off',
          }}
          placeholder="Select"
          showQueryInput
          returnItem
          isLazy
          prependItems={
            canCreateContact
              ? [
                  {
                    item: CONTACTS.NEW_CONTACT,
                    isAction: true,
                    hasDivider: true,
                    onClick: ({ onClose }) => {
                      onClose && onClose();
                      handleOnNewContact
                        ? handleOnNewContact()
                        : contactModalState.onOpen();
                    },
                  },
                ]
              : []
          }
          renderItemContent={contactSelectRenderItem}
          ref={ref}
          {...(serverSideProps || {})}
          {...(clientSideProps || {})}
          {...rest}
        />
        {contactModalState.open && accountId && (
          <ContactFormModal
            isOpen
            accountId={accountId}
            onClose={() => contactModalState.onClose()}
            onCreatedContact={handleContactModalClose}
            defaultValues={defaultValues}
          />
        )}
      </>
    );
  },
);
